<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import { camelCase } from 'es-toolkit'

  import { useFirebaseApp } from 'vuefire'

  import { fetchAndActivate, getRemoteConfig, getString, onConfigUpdate } from 'firebase/remote-config'
  import type { ConfigUpdate } from 'firebase/remote-config'

  import { useAppStore } from '#ourahub-intra/stores/application'

  const fbApp = useFirebaseApp()

  const ouraHubStore = useAppStore()

  const newChangesAvailable = ref(false)

  const env = import.meta.env.VITE_APP_ENV || 'preview'

  const ouraHubVersion = import.meta.env.VITE_APP_COMMIT_HASH || ''

  // Always update any new remote config value changes immediately
  const disableConfigUpdates = false

  const disableVersionUpdates = import.meta.env.DEV || env === 'preview'

  let unsubscribeRemoteConfig: (() => void) | null = null

  watch(
    () => ouraHubStore.activeService,
    () => {
      if (!disableVersionUpdates) {
        newChangesAvailable.value = isDifferentVersion()
      }
    },
    { immediate: true },
  )

  function loadNewVersion() {
    const remoteConfig = getRemoteConfig(fbApp)

    fetchAndActivate(remoteConfig).finally(() => {
      const latestVersion = getString(remoteConfig, `ourahubIntraVersion`)

      sessionStorage.setItem(`ourahubIntraVersion`, latestVersion)

      console.debug('Updating ourahub version', latestVersion)

      if (ouraHubStore.activeService) {
        const activeServiceKey = camelCase(ouraHubStore.activeService.replace('outo-', ''))

        const latestServiceVersion = getString(remoteConfig, `${activeServiceKey}Version`)

        sessionStorage.setItem(`${activeServiceKey}Version`, latestServiceVersion)

        console.debug(`Updating ${ouraHubStore.activeService} version`, latestServiceVersion)
      }

      window.location.reload()
    })
  }

  function isDifferentVersion() {
    let activeServiceNewVersion = false

    const remoteConfig = getRemoteConfig(fbApp)

    // For ourahub version we check the loaded version and use session to prevent multiple reloads

    const activeOuraHubVersion = sessionStorage.getItem(`ourahubIntraVersion`)
    const latestOuraHubVersion = getString(remoteConfig, `ourahubIntraVersion`)

    console.debug('Active ouraHub version', ouraHubVersion)
    console.debug('Latest ouraHub version', latestOuraHubVersion)

    const ouraHubNewVersion =
      !!ouraHubVersion &&
      !!latestOuraHubVersion &&
      ouraHubVersion !== latestOuraHubVersion &&
      activeOuraHubVersion !== latestOuraHubVersion

    if (!activeOuraHubVersion) {
      sessionStorage.setItem(`ourahubIntraVersion`, ouraHubVersion)
    }

    if (ouraHubStore.activeService) {
      // For active service we do not know the loaded version so we relay on session tracking only

      const activeServiceKey = camelCase(ouraHubStore.activeService.replace('outo-', ''))

      const activeServiceVersion = sessionStorage.getItem(`${activeServiceKey}Version`)
      const latestServiceVersion = getString(remoteConfig, `${activeServiceKey}Version`)

      activeServiceNewVersion =
        !!activeServiceVersion && !!latestServiceVersion && activeServiceVersion !== latestServiceVersion

      console.debug(`Active ${ouraHubStore.activeService} version`, activeServiceVersion)
      console.debug(`Latest ${ouraHubStore.activeService} version`, latestServiceVersion)

      if (!activeServiceVersion) {
        sessionStorage.setItem(`${activeServiceKey}Version`, latestServiceVersion)
      }
    }

    return ouraHubNewVersion || activeServiceNewVersion
  }

  onMounted(() => {
    const remoteConfig = getRemoteConfig(fbApp)

    unsubscribeRemoteConfig = onConfigUpdate(remoteConfig, {
      next: (update: ConfigUpdate) => {
        const updatedKeys = Array.from(update.getUpdatedKeys().values())

        const activeServiceKey = ouraHubStore.activeService
          ? camelCase(ouraHubStore.activeService.replace('outo-', ''))
          : ''

        console.debug('Remote Config updated!', activeServiceKey, updatedKeys)

        if (
          !disableConfigUpdates ||
          updatedKeys.includes('ourahubIntraVersion') ||
          updatedKeys.includes(`${activeServiceKey}Version`)
        ) {
          newChangesAvailable.value = true
        }
      },
      error: (error: Error) => {
        console.error(error)
      },
      complete: () => {},
    })

    window.addEventListener('beforeunload', () => {
      if (unsubscribeRemoteConfig) {
        unsubscribeRemoteConfig()
      }
    })
  })

  onBeforeUnmount(() => {
    if (unsubscribeRemoteConfig) {
      unsubscribeRemoteConfig()
    }
  })
</script>

<template lang="html">
  <v-snackbar location="end bottom" :timeout="-1" :model-value="newChangesAvailable">
    New update available

    <template #actions>
      <o-btn-primary class="px-4" @click="loadNewVersion()">Refresh</o-btn-primary>
    </template>
  </v-snackbar>
</template>
